import React, { useState, useRef, useEffect, useCallback } from 'react';
import HorizontalScroll from './components/HorizontalScroll';
import TierNavigationItem from './components/TierNavigationItem';
import CenteredContainer from 'components/CenteredContainer';
import { useCheckout } from 'features/events/checkout/context';
import getHeaderOffset from './components/TierNavigationItem/helpers/getHeaderOffset';
import getFirstVisibleSectionId from './components/TierNavigationItem/helpers/getFirstVisibleSectionId';
import setHistoryState from './components/TierNavigationItem/helpers/setHistoryState';
import computeScrollToSectionId from './components/TierNavigationItem/helpers/computeScrollToSectionId';
import ScrollIncrementsButtons from './components/ScrollIncrementsButtons';
import TierMenu from './components/TierMenu';
import { CheckoutSteps } from 'features/events/checkout/types';

type Props = {
  headerRef: React.RefObject<HTMLDivElement>;
};

export default function HorizontalNavBar({ headerRef }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const bounceHeader = document.getElementById('bounce-header');
  const {
    ticketTiers: { menuSections: allTierSections },
    addonTiers: { menuSections: addonSections },
    currentStep
  } = useCheckout();

  const isAddonStep = currentStep === CheckoutSteps.TicketTiersAddon;
  const sections = isAddonStep ? addonSections : allTierSections;

  const [headerOffset, setHeaderOffset] = useState(
    getHeaderOffset(headerRef, bounceHeader)
  );
  const [activeSectionId, setActiveSectionId] = useState<string>(
    sections[0]?.id || ''
  );

  // Scroll the horizontal scroll menu to a specific position
  const scrollMenuTo = (value: number) => {
    scrollRef.current?.scrollTo({
      left: value,
      behavior: 'smooth'
    });
  };

  // Handle scroll to section on menu item click
  const handleSectionClick = useCallback(
    (sectionId: string) => {
      if (computeScrollToSectionId(sectionId, headerOffset)) {
        setActiveSectionId(sectionId);
      }
    },
    [headerOffset]
  );

  // Handle scroll to active section in menu
  useEffect(() => {
    if (!scrollRef.current) {
      return;
    }

    const sectionMenuItem = document.getElementById(
      `${activeSectionId}-menu-item`
    );

    const sectionMenuItemRect = sectionMenuItem?.getBoundingClientRect();
    const sectionMenuItemOffset = sectionMenuItemRect?.left;
    scrollMenuTo(scrollRef.current.scrollLeft + sectionMenuItemOffset);
  }, [activeSectionId]);

  // Handle set active menu item on scroll and resize
  useEffect(() => {
    let isScrolling: NodeJS.Timeout;
    let isResizing: NodeJS.Timeout;

    const handleWindowScroll = () => {
      clearTimeout(isScrolling);
      isScrolling = setTimeout(() => {
        const isScrolledToBottom =
          window.innerHeight + window.scrollY >=
          document.documentElement.scrollHeight;

        if (isScrolledToBottom) {
          const lastSectionId = sections[sections.length - 1]?.id;

          if (lastSectionId) {
            setHistoryState(lastSectionId);
            setActiveSectionId(lastSectionId);
          }
        } else {
          const firstVisibleSectionId = getFirstVisibleSectionId(
            sections,
            headerOffset
          );
          if (firstVisibleSectionId) {
            setHistoryState(firstVisibleSectionId);
            setActiveSectionId(firstVisibleSectionId);
          }
        }
      }, 100);
    };

    // Handle header height change between viewports
    const handleResize = () => {
      clearTimeout(isResizing);
      isResizing = setTimeout(() => {
        const newHeaderOffset = getHeaderOffset(headerRef, bounceHeader);
        setHeaderOffset(newHeaderOffset);

        const firstVisibleSectionId = getFirstVisibleSectionId(
          sections,
          newHeaderOffset
        );

        if (firstVisibleSectionId) {
          setHistoryState(firstVisibleSectionId);
          setActiveSectionId(firstVisibleSectionId);
        }
      }, 100);
    };

    window.addEventListener('scroll', handleWindowScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(isScrolling);
      clearTimeout(isResizing);
      window.removeEventListener('scroll', handleWindowScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [sections, headerOffset]);

  // Handle header height on load
  useEffect(() => {
    setHeaderOffset(getHeaderOffset(headerRef, bounceHeader));
  }, [headerRef.current?.offsetHeight, bounceHeader?.offsetHeight]);

  // Handle scroll to section using hash on load
  useEffect(() => {
    // Get the hash value from the URL (without the '#' character)
    const hash = window.location.hash.substring(1);

    if (hash) {
      // Find the section ID from the hash (removing '-section' suffix if present)
      const sectionId = hash.endsWith('-section')
        ? hash.replace('-section', '')
        : null;

      if (sectionId && computeScrollToSectionId(sectionId, headerOffset)) {
        setActiveSectionId(sectionId);
      }
    }
  }, []);

  return (
    <div className="overflow-hidden relative border-b-1 border-neutral-200">
      <CenteredContainer hasPadding={false} wideContent className="flex">
        <div className="flex-1 flex overflow-hidden pr-2">
          <HorizontalScroll scrollRef={scrollRef}>
            <div className="flex min-w-max flex-1">
              {sections.map((section) => (
                <TierNavigationItem
                  key={section.id}
                  id={section.id}
                  label={section.name}
                  active={section.id === activeSectionId}
                  onClick={handleSectionClick}
                />
              ))}
            </div>
          </HorizontalScroll>
        </div>
        <div className="flex z-10 shadow-[0px_0_15px_15px_#fff] gap-2">
          {scrollRef.current && (
            <ScrollIncrementsButtons
              scrollRef={scrollRef.current}
              scrollMenuTo={scrollMenuTo}
            />
          )}
          <TierMenu
            activeSectionId={activeSectionId}
            onClick={handleSectionClick}
          />
        </div>
      </CenteredContainer>
    </div>
  );
}
